import type { BrandId } from "./brand"
import type { CountryCode } from "./countries"
import type { PlanAccess, SubscriptionPaymentInterval } from "./payment"
import type { StudentEmailId } from "./studentEmail"

export const platformIntents = ["course", "ebook", "coaching", "community"] as const
export type PlatformIntent = (typeof platformIntents)[number]

export const platformFindSources = [
	"landing",
	"search-engine",
	"friend",
	"blog",
	"influencer",
	"social-media",
	"marketplace",
	"other",
] as const
export type PlatformFindSource = (typeof platformFindSources)[number]

export const themeIds = ["legacy", "modern"] as const
export type ThemeId = (typeof themeIds)[number]

export const customLegalDocumentTypes = ["termsOfService", "privacyPolicy", "cookiePolicy"] as const
export type CustomLegalDocumentType = (typeof customLegalDocumentTypes)[number]
export const customLegalDocumentTypeLabels: Record<CustomLegalDocumentType, string> = {
	termsOfService: "Terms of Service",
	privacyPolicy: "Privacy Policy",
	cookiePolicy: "Cookie Policy",
}
export const customTermsSupplementType = "termsSupplement"

export type PlatformCommunity = {
	isGamificationEnabled: boolean
	isMemberMessagingEnabled: boolean
	isCommunityModerationEnabled: boolean
} & ({ all: true } | { all: false; membershipIds: number[] })

export type PlatformTrustSafety = { suspendedDate: Date; reason: string | null }

export interface PublicPlatform {
	id: number
	brandId: BrandId
	specialPlatform: "bokadero" | null
	name: string
	slug: string
	theme: { id: "legacy" } | { id: "modern"; showCoursePageBackground: boolean }
	language: PlatformLanguageId
	logo: { filePath: string; isIcon: boolean } | null
	title: string | null
	description: string | null
	isSignupsOpen: boolean
	homeLink: string | null
	contactEmail: string | null
	domain: { landingPage: string | null; platform: string }
	primaryColor: { value: string; isCustom: boolean }
	currency: string
	thumbnailFilePath: string | null
	content: {
		courses: boolean
		memberships: boolean
		sessions: boolean
		events: boolean
		videos: boolean
		downloadables: boolean
		bundles: boolean
		certificates: boolean
		multipleChannels: boolean
	}
	paymentGateway:
		| ({ isEnabled: boolean } & ({ type: "stripe" } | { type: "paypal"; id: string; hasDiscountCodes: boolean }))
		| null
	community: PlatformCommunity | null
	matomoSiteId: string | null
	metaPixelId: string | null
	phoneNumberCollection: { defaultCountry: string | null } | null
	trustSafety: PlatformTrustSafety | null
	hasWatermark: boolean
	isContractWithdrawalEnabled: boolean
}

export type PlatformAccess = { [key in PlanAccess]: boolean }

type PlatformPlanBase = {
	commission: number
	access: PlatformAccess
	maxAdmins: number | null
	maxProductAccessEmails: number | null
	maxAiCoursesMonthly: number
}

export type FreePlatformPlan = PlatformPlanBase & { type: "free"; id: null }
export type RegularPlatformPlan = PlatformPlanBase & {
	type: "regular"
	id: number
	endDate: Date | null
} & ({ gateway: "polar"; polarId: string } | { gateway: "stripe"; stripeId: string })

type Addon =
	| { type: "trial"; endDate: Date }
	| ({
			type: "scale" | "noCommission"
			paymentInterval: SubscriptionPaymentInterval
			endDate: Date | null
	  } & ({ gateway: "polar"; polarId: string } | { gateway: "stripe"; stripeId: string }))
export type AppSumoPlatformPlan = PlatformPlanBase & {
	type: "appsumo"
	id: number
	tier: number
	label: string
	addon: Addon | null
}

export type VieDeDinguePlatformPlan = PlatformPlanBase & {
	type: "viededingue"
	id: number
	tier: number
	label: string
	addon: Addon | null
}

export type ProductCanyonPlatformPlan = Omit<AppSumoPlatformPlan, "type"> & { type: "productcanyon" }

export const platformLanguageIds = ["en", "bs", "sv", "es", "de", "fr", "it", "nl", "pt", "pt-BR", "tr"] as const
export type PlatformLanguageId = (typeof platformLanguageIds)[number]
export const platformLanguages: Record<PlatformLanguageId, { label: string; flag: string }> = {
	en: { label: "English", flag: "🇬🇧" },
	bs: { label: "Bosnian", flag: "🇧🇦" },
	fr: { label: "French", flag: "🇫🇷" },
	de: { label: "German", flag: "🇩🇪" },
	it: { label: "Italian", flag: "🇮🇹" },
	nl: { label: "Dutch", flag: "🇳🇱" },
	pt: { label: "Portuguese", flag: "🇵🇹" },
	"pt-BR": { label: "Portuguese (Brazil)", flag: "🇧🇷" },
	es: { label: "Spanish", flag: "🇪🇸" },
	sv: { label: "Swedish", flag: "🇸🇪" },
	tr: { label: "Turkish", flag: "🇹🇷" },
}

/** Shown for the unnamed default community channel, in the academy's language. */
export const defaultCommunityChannelNames: Record<PlatformLanguageId, string> = {
	en: "General",
	bs: "Općenito",
	fr: "Général",
	de: "Allgemein",
	it: "Generale",
	nl: "Algemeen",
	pt: "Geral",
	"pt-BR": "Geral",
	es: "General",
	sv: "Allmänt",
	tr: "Genel",
}

export function getDefaultCommunityChannelName(language: string | null): string {
	return defaultCommunityChannelNames[platformLanguageIds.find(x => x === language) ?? "en"]
}

export const enabledPlatformFeatures = [
	"invite-to-course",
	"block-signups",
	"drip-content",
	"certificates",
	"ai-generated-courses",
] as const
export type EnabledPlatformFeature = (typeof enabledPlatformFeatures)[number]

export interface Platform {
	id: number
	brandId: BrandId
	specialPlatform: "bokadero" | null
	name: string
	slug: string
	theme: { id: "legacy"; hasChosen: boolean } | { id: "modern"; showCoursePageBackground: boolean }
	language: PlatformLanguageId
	logo: { filePath: string; isIcon: boolean } | null
	faviconPath: string | null
	title: string | null
	description: string | null
	homeLink: string | null
	isSignupsOpen: boolean
	contactEmail: string | null
	domain: {
		landingPage: string | null
		platform: string
		hasCustomDomain: boolean
		isLegacyDnsHost: boolean
		hasUnconfiguredDnsRecords: boolean
	}
	reviveSiteSlug: string | null
	ucraftSiteDeleteDate: Date | null
	primaryColor: { value: string; isCustom: boolean }
	currency: string
	thumbnailFilePath: string | null
	landingPage: { type: "revive" | "migrating" } | { type: "landing-page"; showMigratedNotice: boolean } | null
	content: {
		courses: boolean
		legacyExams: boolean
		memberships: boolean
		sessions: boolean
		events: boolean
		videos: boolean
		downloadables: boolean
		bundles: boolean
		certificates: boolean
		multipleChannels: boolean
	}
	enabledStudentEmails: StudentEmailId[]
	paymentGateway:
		| ({ isEnabled: boolean } & (
				| { type: "stripe"; accountType: "standard" | "express"; id: string }
				| { type: "paypal"; id: string; connectDate: Date; hasDiscountCodes: boolean }
		  ))
		| null
	community: PlatformCommunity | null
	matomoSiteId: string | null
	metaPixelId: string | null
	trustSafety: PlatformTrustSafety | null
	plan:
		| FreePlatformPlan
		| RegularPlatformPlan
		| AppSumoPlatformPlan
		| VieDeDinguePlatformPlan
		| ProductCanyonPlatformPlan
	trialEndDate: Date | null
	trialDismissDate: Date | null
	isFromBokadero: boolean
	phoneNumberCollection: { defaultCountry: string | null } | null
	/** The country most of the creators' IP addresses resolve to, for prefilling country inputs. */
	detectedCountry: CountryCode | null
	isReferralEnabled: boolean
	hasChosenIntent: boolean
	hasChosenFindSource: boolean
	hasReachedAdminsLimit: boolean
	polarCustomerId: string | null
	stripeCustomerId: string | null
	aiCreditBalance: number
	aiBetaCreditGrantDate: Date | null
	aiBetaBannerDismissDate: Date | null
	creatorCommunityBannerDismissDate: Date | null
	creatorHighlightsPopupDismissDate: Date | null
	customEmailSender: { domain: string; senderUsername: string; isVerified: boolean } | null
	hasLegacyEmailSender: boolean
	/** When the creator opted in to the statutory withdrawal function, or null while it is off. */
	contractWithdrawalEnableDate: Date | null
	enabledFeatures: EnabledPlatformFeature[]
	createDate: Date
}

export type PlatformContentIds = {
	courseIds: number[]
	videoIds: number[]
	downloadableIds: number[]
	eventIds: number[]
	sessionIds: number[]
	membershipIds: number[]
}
